import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { updateLicenseTypeQuestion } from "@/hera-api/client/sdk.gen";
import type {
  JurisdictionWithLicenseTypeConfigurationResponseSchema,
  NonRegistrantQuestionResponseSchema,
  QuestionScope,
} from "@/hera-api/client/types.gen";
import { useQuestionsForm } from "@/hooks/use-question-form";
import type { DashboardQuestionType } from "@/lib/filter-dashboard-questions";
import {
  QUESTION_FLOW_DEFAULT_HANDLE,
  QUESTION_FLOW_INITIAL_MIN_ZOOM,
  QUESTION_FLOW_OPTION_HANDLE_PREFIX,
  QUESTION_FLOW_SCREEN_MODES,
  QUESTION_FLOW_SCREEN_MODE_EMPTY_MESSAGES,
  QUESTION_FLOW_SCREEN_MODE_LABELS,
  type QuestionFlowContainer,
  type QuestionFlowDesignScope,
  type QuestionFlowDraft,
  type QuestionFlowEdgeKind,
  type QuestionFlowScreenMode,
  type QuestionFlowViewport,
  applyOverlapResolutionToFlowNodes,
  applyQuestionFlowConnection,
  buildQuestionFlowGraph,
  constrainFreeNodeOutsideContainers,
  designScopeLabel,
  diffQuestionFlowPatches,
  extractPersistedLayoutFromNodes,
  filterQuestionsForFlowScreen,
  fitContainersToChildNodes,
  getFlowBranchOptionKeys,
  isQuestionFlowContainerId,
  jurisdictionSupportsLicenseType,
  listLicenseTypesForJurisdiction,
  loadQuestionFlowPersistedLayout,
  mergeContainerDragNodes,
  mergeDraggedNodeOntoPrevious,
  mergeVisibleQuestionFlowDrafts,
  questionFlowLayoutStorageKey,
  refreshContainerMinSizesFromChildren,
  saveQuestionFlowPersistedLayout,
  saveQuestionFlowViewport,
  selectQuestionsForDesignScope,
  supportsFlowBranchHandles,
  toQuestionFlowDrafts,
  truncateQuestionLabel,
} from "@/lib/question-flow-designer";
import { cn } from "@/lib/utils";
import {
  Background,
  type Connection,
  Controls,
  type Edge,
  Handle,
  MarkerType,
  MiniMap,
  type Node,
  type NodeProps,
  NodeResizer,
  type OnNodeDrag,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type Viewport,
  useEdgesState,
  useNodesState,
  useOnViewportChange,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  GitBranch,
  Loader2,
  Maximize2,
  Minimize2,
  Plus,
  Save,
  X,
} from "lucide-react";
import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { QuestionForm } from "./question-form";

type QuestionNodeData = {
  question: QuestionFlowDraft;
};

type EndNodeData = {
  label: string;
};

type ContainerNodeData = {
  label: string;
  kind: QuestionFlowContainer["kind"];
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  onResize?: (containerId: string, width: number, height: number) => void;
  onResizeEnd?: (containerId: string) => void;
};

function QuestionFlowNode({ data }: NodeProps) {
  const question = (data as QuestionNodeData).question;
  const branchOptions = getFlowBranchOptionKeys(question);
  return (
    <div className="w-72 rounded-lg border bg-card shadow-sm">
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2 !bg-background !border-neutral-400"
      />
      <div className="flex items-start justify-between gap-2 border-b px-3 py-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Order {question.display_order}
          </p>
          <p className="text-sm font-semibold leading-snug text-foreground">
            {truncateQuestionLabel(question.question)}
          </p>
        </div>
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          {question.answer_type.replaceAll("_", " ")}
        </span>
      </div>
      {supportsFlowBranchHandles(question.answer_type) ? (
        <div className="space-y-1 border-t px-3 py-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            If answer
          </p>
          {branchOptions.map((option) => (
            <div
              key={option}
              className="relative flex items-center justify-between rounded-md bg-muted/60 px-2 py-1 text-xs"
            >
              <span className="truncate pr-4">{option}</span>
              <Handle
                id={`${QUESTION_FLOW_OPTION_HANDLE_PREFIX}${option}`}
                type="source"
                position={Position.Right}
                className="!right-[-5px] !h-2.5 !w-2.5 !border-2 !bg-sky-500 !border-white"
              />
            </div>
          ))}
        </div>
      ) : null}
      <div className="relative border-t px-3 py-2 text-center text-[11px] text-muted-foreground">
        Default next
        <Handle
          id={QUESTION_FLOW_DEFAULT_HANDLE}
          type="source"
          position={Position.Bottom}
          className="!h-2.5 !w-2.5 !border-2 !bg-emerald-600 !border-white"
        />
      </div>
    </div>
  );
}

function EndFlowNode() {
  return (
    <div className="flex h-16 w-40 items-center justify-center rounded-full border-2 border-dashed border-neutral-400 bg-neutral-50 text-sm font-semibold text-neutral-700">
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2 !bg-background !border-neutral-400"
      />
      End
    </div>
  );
}

function ContainerFlowNode({ id, data, selected, width, height }: NodeProps) {
  const container = data as ContainerNodeData;
  const boxWidth = width ?? container.width;
  const boxHeight = height ?? container.height;
  const [hovered, setHovered] = useState(false);
  const [resizing, setResizing] = useState(false);
  const showResizeHandles = selected || hovered || resizing;
  const tone =
    container.kind === "global"
      ? "border-emerald-300 bg-emerald-50/70"
      : container.kind === "group"
        ? "border-sky-300 bg-sky-50/70"
        : "border-amber-300 bg-amber-50/70";
  const badge =
    container.kind === "global"
      ? "bg-emerald-100 text-emerald-800"
      : container.kind === "group"
        ? "bg-sky-100 text-sky-800"
        : "bg-amber-100 text-amber-900";
  return (
    <div
      className={cn(
        "relative rounded-xl border-2 border-dashed shadow-sm",
        tone,
      )}
      style={{ width: boxWidth, height: boxHeight }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <NodeResizer
        minWidth={container.minWidth}
        minHeight={container.minHeight}
        isVisible={showResizeHandles}
        lineClassName={selected ? undefined : "opacity-0 pointer-events-none"}
        color="#64748b"
        onResize={(_event, params) => {
          container.onResize?.(id, params.width, params.height);
        }}
        onResizeStart={() => setResizing(true)}
        onResizeEnd={() => {
          setResizing(false);
          container.onResizeEnd?.(id);
        }}
      />
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span
          className={cn(
            "rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
            badge,
          )}
        >
          {container.label}
        </span>
        <span className="text-[10px] text-muted-foreground">
          Drag or resize box
        </span>
      </div>
    </div>
  );
}

const nodeTypes = {
  question: QuestionFlowNode,
  end: EndFlowNode,
  container: ContainerFlowNode,
};

function edgeStyle(kind: QuestionFlowEdgeKind): Partial<Edge> {
  if (kind === "sequential") {
    return {
      animated: false,
      style: { stroke: "#a3a3a3", strokeDasharray: "5 4" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#a3a3a3" },
    };
  }
  if (kind === "option") {
    return {
      animated: true,
      style: { stroke: "#0284c7" },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#0284c7" },
      labelStyle: { fill: "#0369a1", fontSize: 11, fontWeight: 600 },
    };
  }
  return {
    animated: false,
    style: { stroke: "#059669", strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: "#059669" },
    labelStyle: { fill: "#047857", fontSize: 11, fontWeight: 600 },
  };
}

function toFlowNodes(
  drafts: QuestionFlowDraft[],
  designScope: QuestionFlowDesignScope,
  layoutStorageKey: string,
  onContainerResize?: (
    containerId: string,
    width: number,
    height: number,
  ) => void,
  onContainerResizeEnd?: (containerId: string) => void,
): Node[] {
  const persisted = loadQuestionFlowPersistedLayout(layoutStorageKey);
  const graph = buildQuestionFlowGraph(drafts, {
    designScope,
    persistedLayout: persisted,
  });
  const nodes: Node[] = graph.containers.map((container) => ({
    id: container.id,
    type: "container",
    position: { x: container.x, y: container.y },
    data: {
      label: container.label,
      kind: container.kind,
      width: container.width,
      height: container.height,
      minWidth: container.minWidth,
      minHeight: container.minHeight,
      onResize: onContainerResize,
      onResizeEnd: onContainerResizeEnd,
    } satisfies ContainerNodeData,
    style: {
      width: container.width,
      height: container.height,
      // Nested question nodes must paint above/inside the dashed frame even
      // when slightly past the padded edge during drag/resize.
      overflow: "visible",
    },
    width: container.width,
    height: container.height,
    zIndex: 0,
    selectable: true,
    draggable: true,
  }));

  for (const node of graph.nodes) {
    if (node.kind === "end" || !node.question) {
      nodes.push({
        id: node.id,
        type: "end",
        position: { x: node.x, y: node.y },
        data: { label: "End" } satisfies EndNodeData,
        draggable: true,
        zIndex: 2,
      });
      continue;
    }
    nodes.push({
      id: node.id,
      type: "question",
      position: { x: node.x, y: node.y },
      data: { question: node.question } satisfies QuestionNodeData,
      parentId: node.containerId ?? undefined,
      // Parent growth is handled in onNodeDrag. RF's expandParent rewrites
      // sibling/parent geometry mid-drag and can fling the rest of the canvas
      // off-screen.
      expandParent: false,
      draggable: true,
      zIndex: 2,
    });
  }
  return nodes;
}

function toFlowEdges(drafts: QuestionFlowDraft[]): Edge[] {
  const graph = buildQuestionFlowGraph(drafts);
  return graph.edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    label: edge.label,
    ...edgeStyle(edge.kind),
  }));
}

function ViewportPersistence({
  onViewportEnd,
}: {
  onViewportEnd: (viewport: Viewport) => void;
}) {
  useOnViewportChange({ onEnd: onViewportEnd });
  return null;
}

function QuestionFlowCanvas({
  drafts,
  designScope,
  layoutStorageKey,
  onDraftsChange,
  onQuestionSelect,
  onNewQuestionAtPosition,
}: {
  drafts: QuestionFlowDraft[];
  designScope: QuestionFlowDesignScope;
  layoutStorageKey: string;
  onDraftsChange: (next: QuestionFlowDraft[]) => void;
  onQuestionSelect?: (question: QuestionFlowDraft | null) => void;
  onNewQuestionAtPosition?: (position: { x: number; y: number }) => void;
}) {
  const { fitView, setViewport, getViewport } = useReactFlow();
  const resizeHandlerRef = useRef<
    (containerId: string, width: number, height: number) => void
  >(() => {});
  const resizeEndHandlerRef = useRef<(containerId: string) => void>(() => {});
  const nodesRef = useRef<Node[]>([]);

  const persistLayout = useCallback(
    (currentNodes: Node[], viewport?: QuestionFlowViewport | null) => {
      const existing = loadQuestionFlowPersistedLayout(layoutStorageKey);
      saveQuestionFlowPersistedLayout(
        layoutStorageKey,
        extractPersistedLayoutFromNodes(
          currentNodes,
          viewport ?? existing?.viewport ?? getViewport(),
        ),
      );
    },
    [getViewport, layoutStorageKey],
  );

  const handleContainerResize = useCallback(
    (containerId: string, width: number, height: number) => {
      resizeHandlerRef.current(containerId, width, height);
    },
    [],
  );
  const handleContainerResizeEnd = useCallback((containerId: string) => {
    resizeEndHandlerRef.current(containerId);
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    toFlowNodes(
      drafts,
      designScope,
      layoutStorageKey,
      handleContainerResize,
      handleContainerResizeEnd,
    ),
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(toFlowEdges(drafts));

  nodesRef.current = nodes;

  useEffect(() => {
    resizeHandlerRef.current = (
      containerId: string,
      width: number,
      height: number,
    ) => {
      setNodes((current) => {
        const withSizes = current.map((node) => {
          if (node.id !== containerId) return node;
          return {
            ...node,
            width,
            height,
            style: {
              ...node.style,
              width,
              height,
            },
            data: {
              ...(node.data as ContainerNodeData),
              width,
              height,
              onResize: handleContainerResize,
              onResizeEnd: handleContainerResizeEnd,
            },
          };
        });
        // Respect the user's resize handle; only refresh minimums from the
        // current child positions (NodeResizer enforces those mins).
        return refreshContainerMinSizesFromChildren(withSizes);
      });
    };
    resizeEndHandlerRef.current = (_containerId: string) => {
      setNodes((current) => {
        const withFreshMins = refreshContainerMinSizesFromChildren(current);
        persistLayout(withFreshMins);
        return withFreshMins;
      });
    };
  }, [
    handleContainerResize,
    handleContainerResizeEnd,
    persistLayout,
    setNodes,
  ]);

  useEffect(() => {
    setNodes(
      toFlowNodes(
        drafts,
        designScope,
        layoutStorageKey,
        handleContainerResize,
        handleContainerResizeEnd,
      ),
    );
    setEdges(toFlowEdges(drafts));
  }, [
    drafts,
    designScope,
    handleContainerResize,
    handleContainerResizeEnd,
    layoutStorageKey,
    setEdges,
    setNodes,
  ]);

  useEffect(() => {
    let cancelled = false;
    const persisted = loadQuestionFlowPersistedLayout(layoutStorageKey);
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (persisted?.viewport) {
        void setViewport(persisted.viewport, { duration: 0 });
        return;
      }
      void fitView({
        padding: 0.12,
        duration: 200,
        minZoom: QUESTION_FLOW_INITIAL_MIN_ZOOM,
        maxZoom: 1.5,
      });
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [fitView, layoutStorageKey, setViewport]);

  useEffect(() => {
    return () => {
      saveQuestionFlowViewport(layoutStorageKey, getViewport());
    };
  }, [getViewport, layoutStorageKey]);

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.sourceHandle) return;
      if (isQuestionFlowContainerId(connection.source)) return;
      if (connection.target && isQuestionFlowContainerId(connection.target)) {
        return;
      }
      const next = applyQuestionFlowConnection(
        drafts,
        connection.source,
        connection.sourceHandle,
        connection.target,
      );
      onDraftsChange(next);
    },
    [drafts, onDraftsChange],
  );

  const onEdgeDoubleClick = useCallback(
    (_event: MouseEvent, edge: Edge) => {
      if (!edge.sourceHandle) return;
      const next = applyQuestionFlowConnection(
        drafts,
        edge.source,
        edge.sourceHandle,
        null,
      );
      onDraftsChange(next);
    },
    [drafts, onDraftsChange],
  );

  const onNodeDrag: OnNodeDrag = useCallback(
    (_event, dragged, currentNodes) => {
      if (isQuestionFlowContainerId(dragged.id)) {
        // Do not trust the drag snapshot for nested children — RF can rewrite
        // relative child positions while the parent moves, which makes every
        // question "disappear" outside the box. Merge onto previous state.
        setNodes((previous) =>
          applyOverlapResolutionToFlowNodes(
            mergeContainerDragNodes(previous, dragged, currentNodes),
            dragged.id,
          ),
        );
        return;
      }
      // Same snapshot issue as container drag: RF's `currentNodes` can rewrite
      // other questions' parentId/positions, which then grows boxes and pushes
      // the rest of the canvas off-screen. Only the dragged node moves.
      setNodes((previous) => {
        const merged = mergeDraggedNodeOntoPrevious(previous, dragged);
        const parentId = previous.find(
          (node) => node.id === dragged.id,
        )?.parentId;
        if (parentId) {
          return fitContainersToChildNodes(merged, parentId);
        }
        return constrainFreeNodeOutsideContainers(merged, dragged.id);
      });
    },
    [setNodes],
  );

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, dragged) => {
      setNodes((current) => {
        const parentId = current.find(
          (node) => node.id === dragged.id,
        )?.parentId;
        const next = parentId
          ? fitContainersToChildNodes(current, parentId)
          : refreshContainerMinSizesFromChildren(current);
        persistLayout(next);
        return next;
      });
    },
    [persistLayout, setNodes],
  );

  const handleViewportChangeEnd = useCallback(
    (viewport: Viewport) => {
      saveQuestionFlowViewport(layoutStorageKey, viewport);
    },
    [layoutStorageKey],
  );

  const onMoveEnd = useCallback(
    (_event: unknown, viewport: Viewport) => {
      handleViewportChangeEnd(viewport);
    },
    [handleViewportChangeEnd],
  );

  const { screenToFlowPosition } = useReactFlow();

  const onNodeClick = useCallback(
    (_event: MouseEvent, node: Node) => {
      if (node.type !== "question") return;
      const question = (node.data as QuestionNodeData).question;
      onQuestionSelect?.(question);
    },
    [onQuestionSelect],
  );

  const onPaneClick = useCallback(() => {
    onQuestionSelect?.(null);
  }, [onQuestionSelect]);

  const onPaneContextMenu = useCallback(
    (event: MouseEvent | globalThis.MouseEvent) => {
      event.preventDefault();
      const nativeEvent = event as globalThis.MouseEvent;
      const target = nativeEvent.target as Element;
      const pane = target.closest(".react-flow__pane");
      if (!pane) return;
      const rect = pane.getBoundingClientRect();
      const flowPos = screenToFlowPosition({
        x: nativeEvent.clientX - rect.left,
        y: nativeEvent.clientY - rect.top,
      });
      onNewQuestionAtPosition?.(flowPos);
    },
    [onNewQuestionAtPosition, screenToFlowPosition],
  );

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onEdgeDoubleClick={onEdgeDoubleClick}
      onNodeDrag={onNodeDrag}
      onNodeDragStop={onNodeDragStop}
      onMoveEnd={onMoveEnd}
      onNodeClick={onNodeClick}
      onPaneClick={onPaneClick}
      onPaneContextMenu={onPaneContextMenu}
      minZoom={0.25}
      maxZoom={2}
      proOptions={{ hideAttribution: true }}
      className="bg-neutral-50"
    >
      <ViewportPersistence onViewportEnd={handleViewportChangeEnd} />
      <Background gap={18} size={1} color="#e5e5e5" />
      <Controls showInteractive={false} />
      <MiniMap
        pannable
        zoomable
        className="!bg-white"
        nodeColor={(node) => {
          if (node.type === "end") return "#d4d4d4";
          if (node.type === "container") return "#e2e8f0";
          return "#bbf7d0";
        }}
      />
    </ReactFlow>
  );
}

/** Inline question detail / edit / create panel for the aside. */
function QuestionDetailPanel({
  questionId,
  scope,
  questionType,
  designScope,
  jurisdictions,
  licenseTypes,
  onDone,
}: {
  questionId: string | null;
  scope: QuestionScope | null;
  questionType: DashboardQuestionType;
  designScope: QuestionFlowDesignScope | null;
  jurisdictions: Array<{
    id: string;
    name: string;
    license_type_configurations?: Array<{ license_type_id: string }>;
  }>;
  licenseTypes: { id: string; name: string }[];
  onDone: () => void;
}) {
  const { questionData, isLoading, error, handleSubmit } = useQuestionsForm(
    scope,
    questionId,
    questionType,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-xs text-destructive">
        Could not load question: {error.message}
      </p>
    );
  }

  // Default jurisdiction / license type from the current design scope
  const filterJurisdictionIds =
    designScope?.kind === "jurisdiction" ? [designScope.jurisdictionId] : [];
  const filterLicenseTypeIds = designScope ? [designScope.licenseTypeId] : [];

  const normalizedQuestionData = questionData
    ? {
        ...questionData,
        answer_options: questionData.answer_options ?? {},
        answer_required: questionData.answer_required ?? false,
        display_up_front: questionData.display_up_front ?? false,
        show_on_fees_payment_screen:
          questionData.show_on_fees_payment_screen ?? false,
        select_from_fixed_list: questionData.select_from_fixed_list ?? false,
        information_text: questionData.information_text ?? "",
      }
    : null;

  return (
    <QuestionForm
      questionId={questionId}
      scope={scope as "global" | "group" | "jurisdiction" | null}
      questionData={normalizedQuestionData}
      jurisdictions={jurisdictions}
      licenseTypes={licenseTypes}
      filterJurisdictionIds={filterJurisdictionIds}
      filterLicenseTypeIds={filterLicenseTypeIds}
      onSubmit={async (data) => {
        await handleSubmit(data);
        onDone();
      }}
      onCancel={onDone}
    />
  );
}

export function QuestionFlowDesignerDialog({
  open,
  onOpenChange,
  questions,
  designScope,
  questionType,
  title = "Question flow designer",
  readOnly = false,
  onSaved,
  controls,
  emptyStateMessage,
  jurisdictions = [],
  licenseTypes = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questions: NonRegistrantQuestionResponseSchema[];
  designScope: QuestionFlowDesignScope | null;
  questionType: DashboardQuestionType;
  title?: string;
  readOnly?: boolean;
  onSaved?: () => void;
  controls?: ReactNode;
  emptyStateMessage?: string;
  jurisdictions?: Array<{
    id: string;
    name: string;
    license_type_configurations?: Array<{ license_type_id: string }>;
  }>;
  licenseTypes?: { id: string; name: string }[];
}) {
  // Parent passes questions already filtered for the chosen design scope.
  const effectiveOriginal = useMemo(
    () => toQuestionFlowDrafts(questions),
    [questions],
  );

  const [drafts, setDrafts] = useState<QuestionFlowDraft[]>(effectiveOriginal);
  const [screenMode, setScreenMode] =
    useState<QuestionFlowScreenMode>("questions");
  const [saving, setSaving] = useState(false);

  type AsidePanelMode =
    | { kind: "legend" }
    | { kind: "question"; question: QuestionFlowDraft }
    | { kind: "new" };
  const [asidePanel, setAsidePanel] = useState<AsidePanelMode>({
    kind: "legend",
  });
  const [canvasFullscreen, setCanvasFullscreen] = useState(false);

  const visibleDrafts = useMemo(
    () => filterQuestionsForFlowScreen(drafts, screenMode),
    [drafts, screenMode],
  );

  const layoutStorageKey = designScope
    ? questionFlowLayoutStorageKey(questionType, designScope, screenMode)
    : "";

  useEffect(() => {
    if (open) setDrafts(effectiveOriginal);
  }, [open, effectiveOriginal]);

  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setScreenMode("questions");
      setAsidePanel({ kind: "legend" });
      setCanvasFullscreen(false);
    }
    wasOpen.current = open;
  }, [open]);

  const handleVisibleDraftsChange = useCallback(
    (nextVisible: QuestionFlowDraft[]) => {
      setDrafts((current) =>
        mergeVisibleQuestionFlowDrafts(current, nextVisible),
      );
    },
    [],
  );

  const patches = useMemo(
    () => diffQuestionFlowPatches(effectiveOriginal, drafts),
    [drafts, effectiveOriginal],
  );
  const dirty = patches.length > 0;

  const handleSave = async () => {
    if (readOnly || !dirty) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      for (const patch of patches) {
        await updateLicenseTypeQuestion({
          path: {
            question_scope: patch.scope as QuestionScope,
            question_id: patch.id,
          },
          body: {
            display_order: patch.display_order,
            jump_target: patch.jump_target,
            answer_options: patch.answer_options,
          },
        });
      }
      toast.success(
        patches.length === 1
          ? "Updated 1 question flow"
          : `Updated ${patches.length} question flows`,
      );
      onSaved?.();
      onOpenChange(false);
    } catch (error) {
      toast.error("Could not save question flow", {
        description:
          error instanceof Error ? error.message : "Please try again",
      });
    } finally {
      setSaving(false);
    }
  };

  const scopeTitle = designScope
    ? `${title} · ${designScopeLabel(designScope)}`
    : title;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        elevated
        className="flex h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[95vw]"
      >
        <DialogHeader className="border-b px-4 py-2 pr-14 text-left">
          <DialogTitle className="flex items-center gap-2 text-base">
            <GitBranch className="h-4 w-4 shrink-0" />
            {scopeTitle}
          </DialogTitle>
          {readOnly ? (
            <DialogDescription>
              Viewing only — changes are not saved.
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {controls ? (
          <div className="border-b bg-muted/20 px-4 py-2">{controls}</div>
        ) : null}

        <div
          className={cn(
            "relative flex min-h-0 flex-1 flex-col md:flex-row",
            canvasFullscreen ? "overflow-hidden" : "",
          )}
        >
          <div
            className={cn(
              "relative min-h-[50vh] flex-1 md:min-h-0",
              canvasFullscreen ? "absolute inset-0 min-h-0 w-full" : "",
            )}
          >
            {designScope ? (
              <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
                <fieldset className="m-0 inline-flex rounded-lg border-0 bg-secondary p-[3px] shadow-sm">
                  <legend className="sr-only">Question screen</legend>
                  {QUESTION_FLOW_SCREEN_MODES.map((mode) => (
                    <Button
                      key={mode}
                      type="button"
                      size="xs"
                      variant={screenMode === mode ? "default" : "ghost"}
                      className="h-7 px-2.5"
                      aria-pressed={screenMode === mode}
                      onClick={() => setScreenMode(mode)}
                    >
                      {QUESTION_FLOW_SCREEN_MODE_LABELS[mode]}
                    </Button>
                  ))}
                </fieldset>

                {!readOnly && (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => setCanvasFullscreen((f) => !f)}
                    title={
                      canvasFullscreen
                        ? "Exit canvas fullscreen"
                        : "Canvas fullscreen"
                    }
                  >
                    {canvasFullscreen ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                    <span className="sr-only">
                      {canvasFullscreen
                        ? "Exit canvas fullscreen"
                        : "Canvas fullscreen"}
                    </span>
                  </Button>
                )}
              </div>
            ) : null}
            {!designScope ? (
              <div className="flex h-full items-center justify-center px-8 text-center text-sm text-muted-foreground">
                {emptyStateMessage ??
                  "Select the required filters to design this flow."}
              </div>
            ) : visibleDrafts.length === 0 ? (
              <div className="flex h-full items-center justify-center px-8 text-center text-sm text-muted-foreground">
                {QUESTION_FLOW_SCREEN_MODE_EMPTY_MESSAGES[screenMode]}
              </div>
            ) : (
              <ReactFlowProvider key={layoutStorageKey}>
                <QuestionFlowCanvas
                  key={canvasFullscreen ? "canvas-fullscreen" : "canvas-normal"}
                  drafts={visibleDrafts}
                  designScope={designScope}
                  layoutStorageKey={layoutStorageKey}
                  onDraftsChange={handleVisibleDraftsChange}
                  onQuestionSelect={(q) =>
                    setAsidePanel(
                      q
                        ? { kind: "question", question: q }
                        : { kind: "legend" },
                    )
                  }
                  onNewQuestionAtPosition={() => setAsidePanel({ kind: "new" })}
                />
              </ReactFlowProvider>
            )}
          </div>

          <aside
            className={cn(
              "flex w-full shrink-0 flex-col border-t bg-background md:border-l md:border-t-0",
              asidePanel.kind === "legend" ? "md:w-80" : "md:w-[38rem]",
              canvasFullscreen ? "hidden" : "",
            )}
          >
            {/* Aside header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-sm font-semibold">
                {asidePanel.kind === "question"
                  ? "Edit question"
                  : asidePanel.kind === "new"
                    ? "New question"
                    : "Legend"}
              </h3>
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={() => setAsidePanel({ kind: "new" })}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    New
                  </Button>
                )}
                {asidePanel.kind !== "legend" && (
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() => setAsidePanel({ kind: "legend" })}
                  >
                    <X className="h-3 w-3" />
                    <span className="sr-only">Close</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Aside body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {asidePanel.kind === "question" ? (
                <QuestionDetailPanel
                  key={asidePanel.question.id}
                  questionId={asidePanel.question.id}
                  scope={asidePanel.question.scope as QuestionScope}
                  questionType={questionType}
                  designScope={designScope}
                  jurisdictions={jurisdictions}
                  licenseTypes={licenseTypes}
                  onDone={() => {
                    onSaved?.();
                    setAsidePanel({ kind: "legend" });
                  }}
                />
              ) : asidePanel.kind === "new" ? (
                <QuestionDetailPanel
                  key="new"
                  questionId={null}
                  scope={
                    designScope ? (designScope.kind as QuestionScope) : null
                  }
                  questionType={questionType}
                  designScope={designScope}
                  jurisdictions={jurisdictions}
                  licenseTypes={licenseTypes}
                  onDone={() => {
                    onSaved?.();
                    setAsidePanel({ kind: "legend" });
                  }}
                />
              ) : (
                <>
                  <ul className="space-y-2 text-xs text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm border border-dashed border-emerald-400 bg-emerald-50" />
                      Global box
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm border border-dashed border-sky-400 bg-sky-50" />
                      Group box
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm border border-neutral-300 bg-white" />
                      Jurisdiction question (no box)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-0.5 w-6 border-t-2 border-dashed border-neutral-400" />
                      Sequential (follows order)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-0.5 w-6 bg-emerald-600" />
                      Explicit next question
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-0.5 w-6 bg-sky-600" />
                      Next by answer
                    </li>
                  </ul>
                  <div className="mt-4 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                    Saving writes{" "}
                    <code className="text-[11px]">display_order</code>,{" "}
                    <code className="text-[11px]">jump_target</code>, and
                    per-answer{" "}
                    <code className="text-[11px]">answer_options</code>. Box
                    positions and sizes stay in this browser. Pan and zoom are
                    remembered per scope tab and screen. Click a question node
                    to edit it.{" "}
                    {!readOnly &&
                      "Right-click on the canvas or press New to add a question."}
                  </div>
                  <p
                    className={cn(
                      "mt-4 text-xs",
                      dirty ? "text-amber-700" : "text-muted-foreground",
                    )}
                  >
                    {dirty
                      ? `${patches.length} question${patches.length === 1 ? "" : "s"} changed`
                      : "No unsaved changes"}
                  </p>
                </>
              )}
            </div>
          </aside>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || readOnly || !dirty}
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save flow
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type DesignerTab = QuestionFlowDesignScope["kind"];

const QUESTION_TYPE_OPTIONS = [
  { value: "registration", label: "Registration" },
  { value: "inspection", label: "Inspection" },
  { value: "deregistration", label: "De-registration" },
];

/**
 * The designer dialog is `elevated` (z-11000), so dropdown popovers need to sit
 * above it or they open behind the dialog and look unresponsive.
 */
const DESIGNER_POPOVER_CLASS = "z-[11100]";

function availableLicenseTypes(
  questions: NonRegistrantQuestionResponseSchema[],
  questionType: DashboardQuestionType,
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[] = [],
): Array<{ value: string; label: string }> {
  const options = new Map<string, string>();
  for (const question of questions) {
    if (question.is_deleted || question.question_type !== questionType)
      continue;
    options.set(question.license_type_id, question.license_type_name);
  }
  // Include license types configured on jurisdictions even when they have no
  // questions yet, so a jurisdiction flow can still be started for them.
  for (const jurisdiction of jurisdictions) {
    for (const config of jurisdiction.license_type_configurations ?? []) {
      if (config.deleted_at || options.has(config.license_type_id)) continue;
      options.set(
        config.license_type_id,
        config.license_type_name ?? config.license_type_id,
      );
    }
  }
  return [...options.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function deriveInitialLicenseTypeId(
  options: Array<{ value: string }>,
  contextualIds: string[],
): string {
  const available = new Set(options.map((option) => option.value));
  const contextual = [...new Set(contextualIds)].filter((id) =>
    available.has(id),
  );
  if (contextual.length === 1) return contextual[0];
  if (options.length === 1) return options[0].value;
  return "";
}

function QuestionFlowDesignerWorkspace({
  open,
  onOpenChange,
  questionType,
  allQuestions,
  jurisdictions,
  selectedJurisdictionIds,
  selectedLicenseTypeIds,
  title,
  readOnly = false,
  onSaved,
  initialTab,
  initialQuestionGroupId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionType: DashboardQuestionType;
  allQuestions: NonRegistrantQuestionResponseSchema[];
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[];
  selectedJurisdictionIds: string[];
  selectedLicenseTypeIds: string[];
  title: string;
  readOnly?: boolean;
  onSaved?: () => void;
  initialTab?: DesignerTab;
  initialQuestionGroupId?: string;
}) {
  const [activeTab, setActiveTab] = useState<DesignerTab>(
    initialTab ?? "global",
  );
  const [selectedQuestionType, setSelectedQuestionType] =
    useState<DashboardQuestionType>(questionType);
  const [licenseTypeId, setLicenseTypeId] = useState("");
  const [questionGroupId, setQuestionGroupId] = useState(
    initialQuestionGroupId ?? "",
  );
  const [jurisdictionId, setJurisdictionId] = useState("");
  const [reconcileNotice, setReconcileNotice] = useState<string | null>(null);
  const wasOpen = useRef(false);

  // Both lists stay complete so either side can always be switched; picking an
  // incompatible pair reconciles the other dropdown instead of hiding options.
  const licenseTypeOptions = useMemo(
    () =>
      availableLicenseTypes(allQuestions, selectedQuestionType, jurisdictions),
    [allQuestions, jurisdictions, selectedQuestionType],
  );

  const groupOptions = useMemo(() => {
    const groups = new Map<string, string>();
    for (const question of allQuestions) {
      if (
        question.is_deleted ||
        question.question_type !== selectedQuestionType ||
        (licenseTypeId && question.license_type_id !== licenseTypeId) ||
        !question.scope.startsWith("group") ||
        !question.question_group_id
      ) {
        continue;
      }
      groups.set(
        question.question_group_id,
        question.question_group_name?.trim() || "Unnamed group",
      );
    }
    return [...groups.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allQuestions, licenseTypeId, selectedQuestionType]);

  const jurisdictionOptions = useMemo(
    () =>
      jurisdictions
        .map((jurisdiction) => ({
          value: jurisdiction.id,
          label: jurisdiction.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [jurisdictions],
  );

  useEffect(() => {
    if (open && !wasOpen.current) {
      const openingType = questionType;
      const openingLicenseOptions = availableLicenseTypes(
        allQuestions,
        openingType,
        jurisdictions,
      );
      setSelectedQuestionType(openingType);
      setLicenseTypeId(
        deriveInitialLicenseTypeId(
          openingLicenseOptions,
          selectedLicenseTypeIds,
        ),
      );
      setQuestionGroupId(initialQuestionGroupId ?? "");
      setReconcileNotice(null);
      if (initialTab) {
        setActiveTab(initialTab);
      } else if (selectedJurisdictionIds.length === 1) {
        setActiveTab("jurisdiction");
      } else {
        setActiveTab("global");
      }
      setJurisdictionId(
        selectedJurisdictionIds.length === 1 ? selectedJurisdictionIds[0] : "",
      );
    }
    wasOpen.current = open;
  }, [
    allQuestions,
    initialQuestionGroupId,
    initialTab,
    jurisdictions,
    open,
    questionType,
    selectedJurisdictionIds,
    selectedLicenseTypeIds,
  ]);

  const designScope = useMemo<QuestionFlowDesignScope | null>(() => {
    if (!licenseTypeId) return null;
    if (activeTab === "global") {
      return { kind: "global", licenseTypeId };
    }
    if (activeTab === "group") {
      if (!questionGroupId) return null;
      return { kind: "group", questionGroupId, licenseTypeId };
    }
    if (!jurisdictionId) return null;
    return { kind: "jurisdiction", jurisdictionId, licenseTypeId };
  }, [activeTab, jurisdictionId, licenseTypeId, questionGroupId]);

  const scopedQuestions = useMemo(
    () =>
      designScope
        ? selectQuestionsForDesignScope(allQuestions, designScope, {
            questionType: selectedQuestionType,
            jurisdictions,
          })
        : [],
    [allQuestions, designScope, jurisdictions, selectedQuestionType],
  );

  const handleQuestionTypeChange = (next: DashboardQuestionType) => {
    const nextLicenseOptions = availableLicenseTypes(
      allQuestions,
      next,
      jurisdictions,
    );
    setSelectedQuestionType(next);
    setLicenseTypeId(
      deriveInitialLicenseTypeId(nextLicenseOptions, selectedLicenseTypeIds),
    );
    setQuestionGroupId("");
    setReconcileNotice(null);
  };

  const licenseTypeLabel = (id: string) =>
    licenseTypeOptions.find((option) => option.value === id)?.label ?? id;

  const jurisdictionLabel = (id: string) =>
    jurisdictions.find((jurisdiction) => jurisdiction.id === id)?.name ?? id;

  const handleLicenseTypeChange = (next: string) => {
    setLicenseTypeId(next);
    setQuestionGroupId("");
    if (
      next &&
      jurisdictionId &&
      !jurisdictionSupportsLicenseType(jurisdictions, jurisdictionId, next)
    ) {
      const clearedJurisdiction = jurisdictionLabel(jurisdictionId);
      setJurisdictionId("");
      setReconcileNotice(
        `${clearedJurisdiction} has no ${licenseTypeLabel(next)} configuration, so the jurisdiction was cleared. Pick another jurisdiction.`,
      );
      return;
    }
    setReconcileNotice(null);
  };

  const handleJurisdictionChange = (next: string) => {
    setJurisdictionId(next);
    if (
      !next ||
      !licenseTypeId ||
      jurisdictionSupportsLicenseType(jurisdictions, next, licenseTypeId)
    ) {
      setReconcileNotice(null);
      return;
    }
    const configured = listLicenseTypesForJurisdiction(jurisdictions, next);
    const previousLicenseType = licenseTypeLabel(licenseTypeId);
    setQuestionGroupId("");
    if (configured.length === 1) {
      setLicenseTypeId(configured[0].value);
      setReconcileNotice(
        `${jurisdictionLabel(next)} has no ${previousLicenseType} configuration, so the license type switched to ${configured[0].label}.`,
      );
      return;
    }
    setLicenseTypeId("");
    setReconcileNotice(
      `${jurisdictionLabel(next)} has no ${previousLicenseType} configuration, so the license type was cleared. Pick one of its configured license types.`,
    );
  };

  const emptyStateMessage = !licenseTypeId
    ? "Select a license type to design this flow."
    : activeTab === "group" && !questionGroupId
      ? "Select a question group to design its flow."
      : activeTab === "jurisdiction" && !jurisdictionId
        ? "Select a jurisdiction to see its questions together with the relevant global and group questions."
        : "No active questions match these selections.";

  const controls = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full">
      <div className="flex-none">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as DesignerTab)}
        >
          <TabsList className="h-8">
            <TabsTrigger value="global" className="h-7 px-3 text-xs">
              Global
            </TabsTrigger>
            <TabsTrigger value="group" className="h-7 px-3 text-xs">
              Group
            </TabsTrigger>
            <TabsTrigger value="jurisdiction" className="h-7 px-3 text-xs">
              Jurisdiction
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Label className="shrink-0 text-xs text-muted-foreground">Type</Label>
          <Select
            options={QUESTION_TYPE_OPTIONS}
            value={[selectedQuestionType]}
            onValueChange={(values) => {
              const value = values[0] as DashboardQuestionType | undefined;
              if (value) handleQuestionTypeChange(value);
            }}
            placeholder="Type"
            multiple={false}
            contentClassName={DESIGNER_POPOVER_CLASS}
            className="h-8 w-36 text-xs"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="shrink-0 text-xs text-muted-foreground">
            License
          </Label>
          <Select
            options={licenseTypeOptions}
            value={licenseTypeId ? [licenseTypeId] : []}
            onValueChange={(values) => handleLicenseTypeChange(values[0] ?? "")}
            placeholder="License type"
            multiple={false}
            contentClassName={DESIGNER_POPOVER_CLASS}
            className="h-8 w-44 text-xs"
          />
        </div>
        {activeTab === "group" ? (
          <div className="flex items-center gap-1.5">
            <Label className="shrink-0 text-xs text-muted-foreground">
              Group
            </Label>
            <Select
              options={groupOptions}
              value={questionGroupId ? [questionGroupId] : []}
              onValueChange={(values) => setQuestionGroupId(values[0] ?? "")}
              placeholder="Question group"
              multiple={false}
              contentClassName={DESIGNER_POPOVER_CLASS}
              className="h-8 w-44 text-xs"
            />
          </div>
        ) : null}
        {activeTab === "jurisdiction" ? (
          <div className="flex items-center gap-1.5">
            <Label className="shrink-0 text-xs text-muted-foreground">
              Jurisdiction
            </Label>
            <Select
              options={jurisdictionOptions}
              value={jurisdictionId ? [jurisdictionId] : []}
              onValueChange={(values) =>
                handleJurisdictionChange(values[0] ?? "")
              }
              placeholder="Jurisdiction"
              multiple={false}
              contentClassName={DESIGNER_POPOVER_CLASS}
              className="h-8 w-44 text-xs"
            />
          </div>
        ) : null}
      </div>

      {reconcileNotice ? (
        <p className="w-full text-xs text-amber-700">{reconcileNotice}</p>
      ) : null}
    </div>
  );

  return (
    <QuestionFlowDesignerDialog
      open={open}
      onOpenChange={onOpenChange}
      questions={scopedQuestions}
      designScope={designScope}
      questionType={selectedQuestionType}
      title={title}
      readOnly={readOnly}
      onSaved={onSaved}
      controls={controls}
      emptyStateMessage={emptyStateMessage}
      jurisdictions={jurisdictions}
      licenseTypes={licenseTypeOptions.map((opt) => ({
        id: opt.value,
        name: opt.label,
      }))}
    />
  );
}

/** Filter-driven Design Flow entry used by question dashboards. */
export function QuestionFlowDesignerEntry({
  questionType,
  allQuestions,
  jurisdictions,
  selectedJurisdictionIds,
  selectedLicenseTypeIds,
  title,
  readOnly = false,
  onSaved,
}: {
  questionType: DashboardQuestionType;
  allQuestions: NonRegistrantQuestionResponseSchema[];
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[];
  selectedJurisdictionIds: string[];
  selectedLicenseTypeIds: string[];
  title: string;
  readOnly?: boolean;
  onSaved?: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        <GitBranch className="mr-2 h-4 w-4" />
        Design Flow
      </Button>
      <QuestionFlowDesignerWorkspace
        open={open}
        onOpenChange={setOpen}
        questionType={questionType}
        allQuestions={allQuestions}
        jurisdictions={jurisdictions}
        selectedJurisdictionIds={selectedJurisdictionIds}
        selectedLicenseTypeIds={selectedLicenseTypeIds}
        title={title}
        readOnly={readOnly}
        onSaved={onSaved}
      />
    </>
  );
}

/** Open Design Flow for a specific question group (from group configuration). */
export function QuestionGroupFlowDesignerButton({
  questionType,
  questionGroupId,
  allQuestions,
  jurisdictions = [],
  title,
  readOnly = false,
  onSaved,
}: {
  questionType: DashboardQuestionType;
  questionGroupId: string;
  allQuestions: NonRegistrantQuestionResponseSchema[];
  jurisdictions?: JurisdictionWithLicenseTypeConfigurationResponseSchema[];
  title?: string;
  readOnly?: boolean;
  onSaved?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const groupQuestion = allQuestions.find(
    (question) =>
      question.question_group_id === questionGroupId &&
      question.question_type === questionType &&
      !question.is_deleted,
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={!questionGroupId}
      >
        <GitBranch className="mr-2 h-4 w-4" />
        Design Flow
      </Button>
      <QuestionFlowDesignerWorkspace
        open={open}
        onOpenChange={setOpen}
        questionType={questionType}
        allQuestions={allQuestions}
        jurisdictions={jurisdictions}
        selectedJurisdictionIds={[]}
        selectedLicenseTypeIds={
          groupQuestion ? [groupQuestion.license_type_id] : []
        }
        initialTab="group"
        initialQuestionGroupId={questionGroupId}
        title={title ?? "Question flow designer"}
        readOnly={readOnly}
        onSaved={onSaved}
      />
    </>
  );
}
